import { useLocation } from "react-router-dom";
import { Routes } from "@/lib/router";
import { useProjectSelectionStore } from "@/lib/store/project-selection";

interface UseTabBarVisibilityOptions {
  /**
   * Additional routes where the tab bar should be hidden. Can be exact paths or startsWith
   * patterns.
   */
  additionalHiddenRoutes?: Array<{
    path: string;
    /** If true, uses startsWith() matching. Otherwise, uses exact equality. */
    matchStartsWith?: boolean;
    /** Optional condition function for dynamic hiding */
    condition?: () => boolean;
  }>;
}

/**
 * Hook to determine if the tab bar should be hidden based on current route and application state
 * (e.g., active selections).
 *
 * @example
 *   ```tsx
 *   const hideTabBar = useTabBarVisibility({
 *     additionalHiddenRoutes: [
 *       { path: '/custom/route', matchStartsWith: true }
 *     ]
 *   });
 *   ```;
 */
export function useTabBarVisibility(options: UseTabBarVisibilityOptions = {}) {
  const locationObj = useLocation();
  const location = "action" in locationObj ? locationObj.location : locationObj;
  const { hasActiveSelection } = useProjectSelectionStore();

  // System routes - always hide
  const isOnSwitchOrganization = location.pathname === "/app/switch-organization";
  // Only the shift detail screen. Singular `/app/shift/`, so the plural list routes
  // (`/app/shifts/upcoming`, `/app/shifts/completed`) are not caught by the prefix and keep
  // their tab bar.
  const isOnShiftDetail = location.pathname.startsWith("/app/shift/");

  // Project detail routes - conditionally hide when selections exist
  const isOnProjectDetail =
    location.pathname.startsWith("/app/projects/") && location.pathname !== "/app/projects";

  // Check additional custom routes
  const isOnAdditionalHiddenRoute = options.additionalHiddenRoutes?.some((route) => {
    const pathMatches = route.matchStartsWith
      ? location.pathname.startsWith(route.path)
      : location.pathname === route.path;

    const conditionMet = route.condition ? route.condition() : true;

    return pathMatches && conditionMet;
  });

  const hideTabBar =
    isOnSwitchOrganization ||
    isOnShiftDetail ||
    (isOnProjectDetail && hasActiveSelection) ||
    Boolean(isOnAdditionalHiddenRoute);

  return hideTabBar;
}
